import { useCallback, useEffect, useRef, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { holdIdleExit } from '../../services/idleHold';
import { updatePlanungsphase } from '../../services/planungsphaseService';
import { fetchSigePlanDokumente, type SigePlanDokumente } from '../../services/sigePlanDokumenteService';
import type { PlanungUnterlage, Planungsphase } from './types';
import { unterlagenSpeicherFehler } from './unterlagenLogic';
import { erzeugeUnterlagenSchreiber, sichtbareListe, type Speicherstand } from './unterlagenSpeicher';

export type { Speicherstand } from './unterlagenSpeicher';

/**
 * Zustand und Speichern der Unterlagen-Checkliste.
 *
 * ═══ WARUM EIN EIGENER STAND NEBEN `phase` ════════════════════════════════
 * `PlanungModul` setzt den Entwurf der Grundlagen bei JEDER Aenderung von
 * `phase` neu auf (Effekt auf `[phase]`). Schriebe die Checkliste ihren
 * gespeicherten Stand per `setPhase` zurueck, verloere jeder, der in den
 * Grundlagen etwas getippt und noch nicht gespeichert hat, seine Eingabe,
 * sobald er im Reiter „Unterlagen" einen Status setzt. Deshalb haelt dieser
 * Hook die Liste selbst und fasst `phase` nie an.
 *
 * Der Datenbankstand aus `phase.unterlagen` wird uebernommen, wenn
 *   - eine andere Planungsphase kommt (anderes Projekt, Erstanlage), oder
 *   - `phase` sich aendert UND hier nichts Ungespeichertes liegt (etwa nach
 *     „Grundlagen speichern" oder einem Neuladen im Lesemodus, wenn ein
 *     anderer Nutzer die Checkliste geaendert hat).
 * Liegt lokal etwas Ungespeichertes, gewinnt der lokale Stand — er ist der
 * juengere.
 *
 * ═══ SPEICHERMUSTER ═══════════════════════════════════════════════════════
 * Wie bei den Fragen: jede Aenderung sofort in der Oberflaeche, dann in die
 * Datenbank — Statuswechsel sofort, Tippen in der Bemerkung entprellt. Die
 * Regeln gegen den Wettlauf alter/neuer Stand stehen im Schreiber
 * (`unterlagenSpeicher.ts`): ein Lauf zur Zeit, der juengste Stand je
 * Planungsphase gewinnt, ein Fehler verwirft nichts, und ein gescheiterter
 * Stand einer anderen Phase wird weder verdraengt noch durch einen
 * spaeteren Erfolg „weggemeldet". Anders als bei den Fragen wird nach einem
 * Fehler NICHT neu geladen — das loeschte genau die Aenderung, die gerade
 * nicht gespeichert werden konnte; die Meldung sagt es, und „Erneut
 * speichern" oder die naechste Aenderung versucht es noch einmal.
 *
 * Geschrieben wird per Teil-Update NUR die Spalte `unterlagen` — ein
 * gleichzeitiges „Grundlagen speichern" schreibt seine Spalten und
 * ueberschreibt diese nicht (und umgekehrt). Gleichzeitiges Bearbeiten durch
 * zwei Nutzer verhindert die Projektsperre: wer sie nicht haelt, sitzt im
 * Lesemodus (`sperrgrund`).
 */

/** Entprellung fuer das Tippen in der Bemerkung. Statuswechsel speichern sofort. */
const TIPP_VERZOEGERUNG_MS = 800;

/**
 * Rueckgabe von `aendere`, wenn die Aenderung eingereiht ist. „Eingereiht"
 * heisst noch nicht „gespeichert": `bestaetigt` erfuellt sich erst, wenn der
 * Stand der Phase in der Datenbank steht, und wird abgelehnt, wenn das
 * Schreiben scheitert (`UnterlagenSchreiber.bestaetigung`). Wer nur die
 * Oberflaeche fortschreibt, prueft die Wahrheit des Rueckgabewerts; wer
 * einen Nachweis stempelt (Versand), wartet auf `bestaetigt`.
 */
export interface AenderungsQuittung {
    bestaetigt: Promise<void>;
}

export const useUnterlagenStand = (phase: Planungsphase | null, sperrgrund: string | null) => {
    const [liste, setListe] = useState<PlanungUnterlage[]>(() => (phase ? phase.unterlagen || [] : []));
    const [gesehen, setGesehen] = useState<Planungsphase | null>(phase);
    const [stand, setStand] = useState<Speicherstand>('gespeichert');
    const [fehler, setFehler] = useState<string | null>(null);
    const [hinweis, setHinweis] = useState<string | null>(null);
    // Ein Schreiber je Modulinstanz — `useState` mit Initialisierer statt
    // `useRef`, damit er beim Rendern nicht jedes Mal neu entsteht.
    const [schreiber] = useState(() => erzeugeUnterlagenSchreiber({
        schreibe: auftrag => updatePlanungsphase(auftrag.phaseId, { unterlagen: auftrag.liste }),
        melde: (s, f) => {
            setStand(s);
            if (f !== undefined) setFehler(f);
        },
        fehlerText: e => unterlagenSpeicherFehler(describeError(e)),
    }));
    /** Juengster lokaler Stand — Grundlage der naechsten Aenderung, auch wenn
     *  React noch nicht neu gezeichnet hat. */
    const listeRef = useRef<PlanungUnterlage[]>(liste);

    // Datenbankstand uebernehmen — im Rendern statt in einem Effekt (React-
    // Muster „Zustand beim Rendern anpassen"): ein Effekt zeichnete erst den
    // alten Stand und dann den neuen. Liegt fuer die Phase noch ein
    // unbestaetigter Stand im Schreiber (gescheitert, wartend, unterwegs),
    // gewinnt ER (`sichtbareListe`) — sonst baute die naechste Aenderung
    // nach einem Hin- und Rueckwechsel auf dem alten Datenbankstand auf und
    // verdraengte die noch nicht gespeicherte erste Aenderung. Die
    // Fehlermeldung des Schreibers bleibt dabei stehen, bis er bestaetigt.
    if (phase !== gesehen) {
        setGesehen(phase);
        const andereAkte = (phase ? phase.id : null) !== (gesehen ? gesehen.id : null);
        if (andereAkte || stand === 'gespeichert') setListe(sichtbareListe(phase, schreiber));
    }

    useEffect(() => {
        listeRef.current = liste;
    }, [liste]);

    const phaseId = phase ? phase.id : null;

    /**
     * Eine Aenderung uebernehmen und das Speichern anstossen.
     * `sofort` fuer Statuswechsel und Verknuepfungen, sonst entprellt.
     * Gibt `false` zurueck, wenn nicht geaendert werden darf, sonst eine
     * Quittung, deren `bestaetigt` die Datenbankbestaetigung abwartet. Auch
     * ohne Aenderung (`neu === alt`) gibt es die Quittung: steht fuer die
     * Phase noch etwas aus, enthaelt es diesen Stand — sonst ist er schon da.
     */
    const aendere = useCallback((
        fn: (alt: PlanungUnterlage[]) => PlanungUnterlage[],
        sofort: boolean,
    ): AenderungsQuittung | false => {
        if (sperrgrund || !phaseId) {
            setHinweis(sperrgrund || 'Die Checkliste kann erst nach dem Speichern der Grundlagen geändert werden.');
            return false;
        }
        const alt = listeRef.current;
        const neu = fn(alt);
        // Die Quittung gehoert zu GENAU dieser Auftragsversion: der Erfolg
        // eines aelteren, schon laufenden Schreibvorgangs bestaetigt sie nicht.
        // Ohne Aenderung gilt die zuletzt vergebene Version — sie traegt den
        // Stand, den der Aufrufer sieht.
        let version: number;
        if (neu !== alt) {
            listeRef.current = neu;
            setListe(neu);
            setHinweis(null);
            version = schreiber.vormerken({ phaseId, liste: neu }, sofort ? 0 : TIPP_VERZOEGERUNG_MS);
        } else {
            version = schreiber.aktuelleVersion(phaseId);
        }
        const bestaetigt = schreiber.bestaetigung(phaseId, version);
        // Wer die Zusage nicht abwartet, soll keine „unhandled rejection"
        // bekommen — der Fehler steht ohnehin als Speichermeldung im Modul.
        bestaetigt.catch(() => undefined);
        return { bestaetigt };
    }, [sperrgrund, phaseId, schreiber]);

    const erneutSpeichern = useCallback(() => {
        void schreiber.speichern();
    }, [schreiber]);

    // Verlaesst der Nutzer das Modul mit einer entprellten Aenderung, wird
    // sie noch geschrieben statt zu verfallen.
    useEffect(() => () => schreiber.abschliessen(), [schreiber]);

    // Der Leerlaufrueckzug darf das Projekt nicht schliessen, solange hier
    // etwas Ungespeichertes liegt — auch nicht nach einem Fehler.
    useEffect(() => {
        if (stand === 'gespeichert') return;
        return holdIdleExit('planungsphase-unterlagen');
    }, [stand]);

    return { liste, stand, fehler, hinweis, setHinweis, aendere, erneutSpeichern };
};

/**
 * Die mitgeltenden Dokumente des aktuellen SiGe-Plans. Laedt beim Oeffnen
 * des Reiters und auf „neu lesen"; `fetchSigePlanDokumente` wirft nie
 * (fail-open).
 *
 * Eine spaete Antwort darf eine juengere nicht ueberschreiben: jeder Lauf
 * gehoert zu genau einem Effekt, und dessen Aufraeumen entwertet ihn — beim
 * Projektwechsel, beim erneuten Lesen und beim Verlassen des Reiters.
 */
export const useSigePlanDokumente = (projectId: string | null) => {
    const [daten, setDaten] = useState<SigePlanDokumente | null>(null);
    const [anstoss, setAnstoss] = useState(0);

    useEffect(() => {
        let aktuell = true;
        void fetchSigePlanDokumente(projectId || '').then(ergebnis => {
            if (aktuell) setDaten(ergebnis);
        });
        return () => { aktuell = false; };
    }, [projectId, anstoss]);

    const neuLaden = useCallback(() => setAnstoss(n => n + 1), []);

    return { daten, laden: daten === null, neuLaden };
};
